import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  RouterProvider,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { requireGuest, requireSession } from "./auth/guards";
import { BuilderWizard } from "./components/BuilderWizard";
import { CaptureViewer } from "./components/CaptureViewer";
import { RunConsole } from "./components/RunConsole";
import { DashboardPage } from "./pages/DashboardPage";
import { AccountPage } from "./pages/AccountPage";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { SettingsVaultPage } from "./pages/SettingsVaultPage";
import { UsagePage } from "./pages/UsagePage";
import { useBuilderStore } from "./store";

function AppShell() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  // The builder store still carries a demo template/draft id (Phase 5 follow-up), but the tenant
  // must come from the authenticated session rather than a hardcoded account.
  useEffect(() => {
    if (!session?.accountId) return;
    useBuilderStore.setState({
      accountId: session.accountId,
      role: session.role ?? "run_only",
    });
  }, [session?.accountId, session?.role]);

  return (
    <div className="min-h-screen bg-[var(--tj-bg)] text-[var(--tj-fg)]">
      <header className="tj-container">
        <nav className="tj-navbar" aria-label="Primary Navigation">
          <Link to="/" className="serif-heading text-xl">
            Task Jinn
          </Link>
          <div className="tj-nav-links">
            {session ? (
              <>
                <Link to="/dashboard">Dashboard</Link>
                <Link to="/account">Account</Link>
                <Link to="/settings">Secrets</Link>
                <Link to="/usage">Usage</Link>
                <span className="tj-badge tj-badge-amber hidden sm:inline-block">
                  {session.email ?? session.role ?? "member"}
                </span>
                <button
                  type="button"
                  className="tj-btn tj-btn-secondary"
                  onClick={() => {
                    signOut();
                    void navigate({ to: "/" });
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="tj-btn tj-btn-primary inline-block">
                  Sign in
                </Link>
                <span className="tj-badge tj-badge-amber hidden sm:inline-block">Tensile Labs</span>
              </>
            )}
          </div>
        </nav>
      </header>
      <Outlet />
    </div>
  );
}

const rootRoute = createRootRoute({ component: AppShell });

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

interface LoginSearch {
  redirect?: string;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: () => requireGuest(),
  component: LoginPage,
});

const builderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates/$templateId/builder",
  validateSearch: (search: Record<string, unknown>) => ({
    step: typeof search.step === "string" ? search.step : undefined,
    connector: typeof search.connector === "string" ? search.connector : undefined,
  }),
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: BuilderRoute,
});

function BuilderRoute() {
  const search = useSearch({ from: builderRoute.id });
  const { session } = useAuth();
  if (search.step === "finalize" && session?.role === "run_only") {
    return (
      <main className="tj-container max-w-xl py-10" data-testid="finalize-denied">
        <div className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-6">
          <span className="tj-badge tj-badge-amber">Owner/Admin only</span>
          <h1 className="serif-heading mt-3 text-2xl">Finalizing is restricted</h1>
          <p className="mt-2 text-sm text-[var(--tj-muted)]">
            Your account role is <strong>Run only</strong>. You can edit a draft and propose a
            finalize, which creates a change request for an Owner or Admin to review.
          </p>
          <Link
            to="/templates/$templateId/builder"
            params={{ templateId: "demo" }}
            search={{ step: undefined, connector: undefined }}
            className="tj-btn tj-btn-primary mt-4 inline-block"
          >
            Back to the builder
          </Link>
        </div>
      </main>
    );
  }
  return <BuilderWizard />;
}

const capturesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$taskId/captures",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: () => {
    const params = capturesRoute.useParams();
    return <CaptureViewer taskRunId={params.taskId} />;
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: DashboardPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: AccountPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: SettingsVaultPage,
});

const usageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/usage",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: UsagePage,
});

const taskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$taskId",
  beforeLoad: ({ location }) => {
    requireSession(location);
  },
  component: () => {
    const params = taskRoute.useParams();
    return <RunConsole taskId={params.taskId} />;
  },
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  loginRoute,
  builderRoute,
  capturesRoute,
  dashboardRoute,
  accountRoute,
  settingsRoute,
  usageRoute,
  taskRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
